import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Building2, User, CreditCard, CheckCircle, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { registrationApi } from '../services/saasApi';
import LanguageSelector from '../components/LanguageSelector';

import Step0CountrySelect from '../components/registration/Step0CountrySelect';
import Step1CompanyInfo from '../components/registration/Step1CompanyInfo';
import Step2AdminContact from '../components/registration/Step2AdminContact';
import Step3PlanSelection from '../components/registration/Step3PlanSelection';
import Step4Payment from '../components/registration/Step4Payment';
import Step5Confirmation from '../components/registration/Step5Confirmation';

const Registration = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState({});

  // Social pre-fill from OAuth redirect (?social_email=...&social_provider=...)
  const socialPrefill = useMemo(() => {
    const email = searchParams.get('social_email');
    if (!email) return null;
    return {
      email,
      first_name: searchParams.get('social_first_name') || '',
      last_name: searchParams.get('social_last_name') || '',
      provider: searchParams.get('social_provider') || '',
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const languageCode = (i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0];
    const isRTL = languageCode === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = languageCode;
  }, [i18n.language, i18n.resolvedLanguage]);

  const [formData, setFormData] = useState({
    country_code: null,
    company_name: '', legal_form: 'SARL', tax_id: '', sector: '', company_size: '11-50', subdomain: '',
    admin_email: socialPrefill?.email || '',
    admin_first_name: socialPrefill?.first_name || '',
    admin_last_name: socialPrefill?.last_name || '',
    admin_phone: '',
    plan_code: 'STARTER', billing_cycle: 'monthly', selected_plan: null,
    skip_payment: true, payment_method: '', payment_gateway: '',
    login_url: '', trial_end_date: '',
  });

  const steps = [
    { number: 0, title: 'Pays', icon: Globe, component: Step0CountrySelect, hideFromProgress: true },
    { number: 1, title: t('registration.steps.step1'), icon: Building2, component: Step1CompanyInfo },
    { number: 2, title: t('registration.steps.step2'), icon: User, component: Step2AdminContact },
    { number: 3, title: t('registration.steps.step3'), icon: CreditCard, component: Step3PlanSelection },
    { number: 4, title: t('registration.steps.step4'), icon: CreditCard, component: Step4Payment },
    { number: 5, title: t('registration.steps.step5'), icon: CheckCircle, component: Step5Confirmation },
  ];

  const handleStep0 = (data) => {
    setFormData((prev) => ({ ...prev, country_code: data.country_code, payment_gateway: '', payment_method: '', skip_payment: true }));
    setCurrentStep(1);
  };

  const handleStep1 = async (data) => {
    setLoading(true); setServerErrors({});
    try {
      const response = await registrationApi.step1(data);
      setFormData((prev) => ({ ...prev, ...data, subdomain: response.subdomain }));
      setSessionId(response.session_id);
      const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'mawered.local';
      toast.success(t('registration.toasts.subdomainAvailable', { subdomain: `${response.subdomain}.${baseDomain}` }));
      setCurrentStep(2);
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message;
      if (error?.fieldErrors?.tax_id) { setServerErrors({ tax_id: error.fieldErrors.tax_id }); }
      else if (typeof errorMessage === 'string' && /matricule|tax_id|tax id|identifiant|siret|ice|cr number/i.test(errorMessage)) {
        setServerErrors({ tax_id: errorMessage });
      }
      toast.error(errorMessage || t('registration.toasts.validationError'));
    } finally { setLoading(false); }
  };

  const handleStep2 = async (data) => {
    setLoading(true); setServerErrors({});
    try {
      const payload = socialPrefill
        ? { ...data, social_provider: socialPrefill.provider }
        : data;
      const response = await registrationApi.step2(sessionId, payload);
      setFormData((prev) => ({ ...prev, ...data }));
      if (response.email_available) { toast.success(t('registration.toasts.emailAvailable')); setCurrentStep(3); }
      else { setServerErrors({ admin_email: t('registration.step2.validation.emailTaken') }); toast.error(t('registration.step2.validation.emailTaken')); }
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message;
      if (errorMessage?.toLowerCase().includes('email') || error?.status === 409) setServerErrors({ admin_email: errorMessage || t('registration.step2.validation.emailTaken') });
      toast.error(errorMessage || t('registration.toasts.validationError'));
    } finally { setLoading(false); }
  };

  const handleStep3 = async (data) => {
    setLoading(true);
    try {
      const response = await registrationApi.step3(sessionId, { plan_code: data.plan_code, billing_cycle: data.billing_cycle });
      setFormData((prev) => ({ ...prev, plan_code: data.plan_code, billing_cycle: data.billing_cycle, selected_plan: response }));
      toast.success(t('registration.toasts.planSelected', { plan: response.plan_name }));
      setCurrentStep(4);
    } catch (error) { toast.error(typeof error === 'string' ? error : error?.message || t('registration.toasts.planSelectionError')); }
    finally { setLoading(false); }
  };

  const handleStep4 = async (data) => {
    setLoading(true);
    try {
      await registrationApi.step4(sessionId, data);
      setFormData((prev) => ({ ...prev, ...data }));
      toast.success(data.skip_payment ? t('registration.toasts.trialActivated') : t('registration.toasts.paymentConfirmed'));
      setCurrentStep(5);
    } catch (error) { toast.error(typeof error === 'string' ? error : error?.message || t('registration.toasts.paymentError')); }
    finally { setLoading(false); }
  };

  const handleStep5 = async () => {
    setLoading(true);
    try {
      const response = await registrationApi.step5(sessionId);
      const subdomain = response.subdomain;
      const scheme = process.env.REACT_APP_TENANT_SCHEME || 'http';
      const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'mawered.local';
      // Social users: initiate OAuth on tenant subdomain so backend extracts
      // the subdomain from Host header and routes to correct tenant DB.
      // Route: subdomain.mawered.local/api/v1/auth/{provider}
      // Traefik strips /api → backend sees /v1/auth/{provider} with correct Host.
      const effectiveLoginUrl = socialPrefill
        ? `${scheme}://${subdomain}.${baseDomain}/api/v1/auth/${socialPrefill.provider}`
        : response.login_url;
      setFormData((prev) => ({ ...prev, login_url: effectiveLoginUrl, trial_end_date: response.trial_end_date, subdomain: subdomain || prev.subdomain, organization_id: response.organization_id }));
      if (!formData.skip_payment && formData.payment_gateway === 'stripe' && subdomain) {
        try {
          const stripeRes = await registrationApi.initiateStripePayment({ subdomain, plan_code: formData.plan_code, billing_cycle: formData.billing_cycle, admin_email: formData.admin_email, admin_first_name: formData.admin_first_name, admin_last_name: formData.admin_last_name });
          toast.success(t('registration.toasts.redirectingToStripe'));
          window.location.href = stripeRes.checkout_url; return null;
        } catch { toast.error(t('registration.toasts.stripeInitError')); }
      }
      // Provisioning now runs server-side in the background; Step5Confirmation
      // polls /register/status until it is ready before showing success.
      return response;
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.message || t('registration.toasts.accountCreationError'));
      throw error;
    } finally { setLoading(false); }
  };

  const handlePrevious = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  const renderStepComponent = () => {
    const stepDef = steps.find((s) => s.number === currentStep);
    if (!stepDef) return null;
    const CurrentStepComponent = stepDef.component;
    return (
      <CurrentStepComponent
        formData={formData} loading={loading}
        serverErrors={(currentStep === 1 || currentStep === 2) ? serverErrors : {}}
        onClearServerError={(field) => { if (serverErrors[field]) setServerErrors((prev) => ({ ...prev, [field]: null })); }}
        onNext={currentStep === 0 ? handleStep0 : currentStep === 1 ? handleStep1 : currentStep === 2 ? handleStep2 : currentStep === 3 ? handleStep3 : currentStep === 4 ? handleStep4 : handleStep5}
        onPrevious={handlePrevious}
        sessionId={sessionId}
        countryCode={formData.country_code}
        socialPrefill={(currentStep === 0 || currentStep === 2) ? socialPrefill : null}
        isSocial={!!socialPrefill}
      />
    );
  };

  const visibleSteps = steps.filter(s => !s.hideFromProgress);

  return (
    <div className="dark min-h-screen" style={{ backgroundColor: "var(--lm-canvas)", fontFamily: "Inter, SF Pro Display, -apple-system, system-ui, sans-serif", position: "relative", overflow: "hidden" }}>
      {/* Ambient glow */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        <div style={{
          position: "absolute", top: "-8%", left: "50%", transform: "translateX(-50%)",
          width: "700px", height: "350px",
          background: "radial-gradient(ellipse, rgba(94,106,210,0.08) 0%, transparent 70%)",
          filter: "blur(70px)",
        }} />
        <div style={{
          position: "absolute", bottom: "10%", right: "-5%",
          width: "400px", height: "300px",
          background: "radial-gradient(ellipse, rgba(94,106,210,0.05) 0%, transparent 70%)",
          filter: "blur(80px)",
        }} />
      </div>

      {/* Header */}
      <div style={{ backgroundColor: "rgba(1,1,2,0.85)", backdropFilter: "blur(12px)", borderBottom: "1px solid #23252a", height: "56px", position: "relative", zIndex: 10 }} className="flex items-center">
        <div className="max-w-5xl mx-auto px-4 w-full flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/brand/mawered-navbar-lockup.svg" alt="Mawered" style={{ height: "22px", width: "auto" }} />
            <div style={{ width: "1px", height: "16px", backgroundColor: "var(--lm-border)" }} />
            <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('registration.header.subtitle')}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="minimal" />
            <button onClick={() => navigate('/')} className="p-1 rounded transition-colors" style={{ color: "var(--lm-ink-2)" }}
              onMouseEnter={e => e.currentTarget.style.color = "var(--lm-ink)"} onMouseLeave={e => e.currentTarget.style.color = "var(--lm-ink-2)"}>
              <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
            </button>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ backgroundColor: "rgba(15,16,17,0.85)", backdropFilter: "blur(8px)", borderBottom: "1px solid #23252a", position: "relative", zIndex: 10 }}>
        <div className="max-w-5xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            {visibleSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              const isLast = index === visibleSteps.length - 1;
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
                      style={{
                        backgroundColor: isCompleted ? "#27a644" : isActive ? "#5e6ad2" : "transparent",
                        border: `1px solid ${isCompleted ? "#27a644" : isActive ? "#5e6ad2" : "var(--lm-border)"}`,
                        boxShadow: isActive ? "0 0 0 3px rgba(94,106,210,0.12)" : "none",
                      }}>
                      {isCompleted
                        ? <Check className="w-3.5 h-3.5 text-white" />
                        : <Icon className="w-3.5 h-3.5" style={{ color: isActive ? "#fff" : "var(--lm-ink-3)" }} />}
                    </div>
                    <p className="mt-1.5 text-xs font-medium text-center max-w-[72px] hidden sm:block"
                      style={{ color: isActive ? "#5e6ad2" : isCompleted ? "#27a644" : "var(--lm-ink-3)", letterSpacing: "0.1px" }}>
                      {step.title}
                    </p>
                  </div>
                  {!isLast && (
                    <div className="flex-1 h-px mx-2 transition-all"
                      style={{ backgroundColor: currentStep > step.number ? "#27a644" : "var(--lm-border)" }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid #23252a" }}>
          {renderStepComponent()}
        </div>
      </div>

      {/* Inline loading bar — replaces the blocking overlay */}
      {loading && currentStep < 5 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 h-0.5" style={{ background: "var(--lm-border)" }}>
          <div className="h-full animate-pulse" style={{ background: "linear-gradient(90deg, #5e6ad2, #828fff, #5e6ad2)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
        </div>
      )}
    </div>
  );
};

export default Registration;
